import { all, call, put, select, takeLatest } from 'redux-saga/effects';

import { Message, messageBannerFunctions, MessageTypes } from '@nhl/nhl-react-ui/lib/MessageBanner';

import { commonMessages } from '~/utility/commonMessages';
import { REGULAR_SEASON_ID } from '~/utility/constants';
import { fetchEncodedData } from '~/utility/requests';
import { translate } from '~/utility/translator';
import utils from '~/views/pages/home/utils';

import { statsConfigSelectors } from '../config';
import seasonSelectors from '../config/selectors/seasonSelectors';

import actionCreators from './actionCreators';
import actionTypes from './actionTypes';

const createLeadersLink =
    (reportType, params) =>
    (filterExp, measure, additionalSort = '') => {
        let leadersLink =
            `/${reportType}?reportName=summary` +
            '&reportType=season' +
            `&sort=${measure}${additionalSort}` +
            `&seasonFrom=${params.seasonId}` +
            `&seasonTo=${params.seasonId}` +
            `&gameType=${params.gameTypeId}` +
            filterExp;
        return params.franchiseId === 0
            ? leadersLink
            : `${leadersLink}&playerPlayedFor=franchise.${params.franchiseId}`;
    };

const createSkaterMeasure = (leadersUrl, params, cayenneExp, leaderExp) => {
    const getAllLeadersLink = createLeadersLink('skaters', params);
    return [
        {
            measure: 'points',
            url: `${leadersUrl}/leaders/skaters/points?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink(leaderExp, 'points', ',a_gamesPlayed'),
        },
        {
            measure: 'goals',
            url: `${leadersUrl}/leaders/skaters/goals?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink(leaderExp, 'goals', ',points,a_gamesPlayed'),
        },
        {
            measure: 'assists',
            url: `${leadersUrl}/leaders/skaters/assists?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink(leaderExp, 'assists', ',points,a_gamesPlayed'),
        },
    ];
};

const createGoalieMeasure = (leadersUrl, params, cayenneExp, leaderExp) => {
    const getAllLeadersLink = createLeadersLink('goalies', params);
    return [
        {
            measure: 'gaa',
            url: `${leadersUrl}/leaders/goalies/gaa?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink(leaderExp, 'a_goalsAgainstAverage'),
        },
        {
            measure: 'savePctg',
            url: `${leadersUrl}/leaders/goalies/savePctg?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink(leaderExp, 'savePct'),
        },
        {
            measure: 'shutouts',
            url: `${leadersUrl}/leaders/goalies/shutouts?cayenneExp=${cayenneExp}`,
            allLeadersLink: getAllLeadersLink('', 'shutouts', ',savePct'),
        },
    ];
};

function* getLeadersData(action) {
    // Franchise Id is not in base table, but it's in team table
    const cayenneExp = utils.getQueryParamsFromState(action.payload, ' and ').replace('franchise', 'team.franchiseId');

    const { seasonId, gameTypeId } = action.payload;
    const statsConfig = yield select(statsConfigSelectors.getConfig);
    const seasonData = seasonSelectors.getDataForSeason(statsConfig, seasonId);

    // For GAA, SV% we need to check if goalie played a certain amount of games, or minutes
    const goalieMinKey =
        gameTypeId === REGULAR_SEASON_ID
            ? 'minimumRegularGamesForGoalieStatsLeaders'
            : 'minimumPlayoffMinutesForGoalieStatsLeaders';
    const goalieMinValue = seasonData[goalieMinKey];
    const goalieExp =
        gameTypeId === REGULAR_SEASON_ID
            ? ` and gamesPlayed >= ${goalieMinValue}`
            : ` and timeOnIce >= ${goalieMinValue * 60}`;
    const goalieLeaderExp =
        gameTypeId === REGULAR_SEASON_ID
            ? `&filter=gamesPlayed,gte,${goalieMinValue}`
            : `&filter=timeOnIce,gte,${goalieMinValue}`;

    const leadersUrl = `${window.__BASE_REST_URL__}`;
    const leaderData = [
        {
            title: 'skater',
            measures: createSkaterMeasure(leadersUrl, action.payload, cayenneExp, ''),
        },
        {
            title: 'goalie',
            measures: createGoalieMeasure(leadersUrl, action.payload, `${cayenneExp}${goalieExp}`, goalieLeaderExp),
            requirements: {
                name: goalieMinKey,
                value: goalieMinValue,
            },
        },
        {
            title: 'defensemen',
            measures: createSkaterMeasure(
                leadersUrl,
                action.payload,
                `${cayenneExp} and player.positionCode = 'D'`,
                '&position=D',
            ),
        },
        {
            title: 'rookie',
            measures: createSkaterMeasure(
                leadersUrl,
                action.payload,
                `${cayenneExp} and isRookie = 'Y'`,
                '&isRookie=1',
            ),
        },
    ];

    try {
        // Create a [] of all the urls we need to call
        let leaderUrls = [];
        leaderData.forEach(({ measures }) => {
            const measureUrls = measures.map((measure) => call(fetchEncodedData, measure.url));
            leaderUrls = [...leaderUrls, ...measureUrls];
        });

        const leaderResponse = yield all(leaderUrls);

        // Add the response back to the leaders data
        leaderData.forEach(({ measures }) => {
            measures.forEach((measure) => {
                measure.leaders = leaderResponse.shift().data;
            });
        });

        yield put(actionCreators.leadersResponse(true, null, leaderData));
    } catch (errors) {
        const errorMessage = translate(commonMessages.misc.unknown);
        messageBannerFunctions.dispatchMessage(new Message(errorMessage, MessageTypes.ERROR));
        yield put(actionCreators.leadersResponse(false, errors, null));
    }
}

export default function* watchLeaders() {
    yield takeLatest(actionTypes.REQUEST_LEADERS, getLeadersData);
}
